<template>
  <section class="testimonials-section" ref="sectionRef">
    <div class="testimonials-inner">
      <!-- Header -->
      <div class="testimonials-header" ref="headerRef">
        <div class="testimonials-header__left">
          <!--          <p class="testimonials-eyebrow">{{ $t('page.testimonials.subtitle') }}</p>-->
          <h2 class="testimonials-title">{{ $t('page.testimonials.title') }}</h2>
        </div>
        <div class="testimonials-header__right">
          <div class="testimonials-progress">
            <div class="testimonials-progress__fill" ref="progressFillRef"></div>
          </div>
        </div>
      </div>

      <!-- Drag hint -->
      <div class="testimonials-hint" ref="hintRef">
        <span class="testimonials-hint__text">drag to explore</span>
        <ArrowRight01Icon class="testimonials-hint__icon" />
      </div>

      <!-- Horizontal cards track -->
      <div class="testimonials-viewport" :class="{ 'is-dragging': isDragging }" ref="viewportRef">
        <div class="testimonials-track" ref="trackRef">
          <article
            v-for="(testimonial, index) in testimonials"
            :key="testimonial.id"
            class="testimonial-card"
            :ref="(el) => (cardRefs[index] = el)"
          >
            <blockquote class="testimonial-card__quote">"{{ testimonial.quote }}"</blockquote>

            <button
              v-if="testimonial.quote.length > 450"
              class="testimonial-card__read-more"
              @click.stop="openModal(testimonial)"
            >
              {{ $t('common.readMore') }}
            </button>

            <footer class="testimonial-card__footer">
              <div class="testimonial-card__author">
                <a
                  v-if="testimonial.linkedin"
                  :href="testimonial.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="testimonial-card__name testimonial-card__name--link"
                >
                  {{ testimonial.name }}
                  <Linkedin01Icon class="testimonial-card__linkedin-icon" />
                </a>
                <span v-else class="testimonial-card__name">{{ testimonial.name }}</span>
                <span class="testimonial-card__sep">·</span>
                <span class="testimonial-card__role">{{ testimonial.role }}</span>
                <span class="testimonial-card__sep">·</span>
                <span class="testimonial-card__company">
                  <img
                    v-if="testimonial.companyLogo"
                    :src="testimonial.companyLogo"
                    :alt="testimonial.company"
                    class="testimonial-card__company-logo"
                  />
                  <span v-else>{{ testimonial.company }}</span>
                </span>
              </div>
            </footer>
          </article>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <TestimonialModal
        :is-open="modalOpen"
        :testimonial="selectedTestimonial"
        @close="modalOpen = false"
      />
    </Teleport>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Linkedin01Icon, ArrowRight01Icon } from 'hugeicons-vue'
import { useI18n } from 'vue-i18n'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import TestimonialModal from '@/components/landingFolio/TestimonialModal.vue'

gsap.registerPlugin(ScrollTrigger)

const { tm } = useI18n()

const sectionRef = ref(null)
const headerRef = ref(null)
const viewportRef = ref(null)
const trackRef = ref(null)
const cardRefs = ref([])
const progressFillRef = ref(null)
const hintRef = ref(null)
const isDragging = ref(false)

const modalOpen = ref(false)
const selectedTestimonial = ref(null)

const testimonials = computed(() => tm('page.testimonials.items'))

let ctx = null
let observer = null
let velocity = 0
let lastX = 0
let lastTime = 0
let inertiaFrame = null
let position = 0
let scrollAnimationFrame = null
let dragDistance = 0
let clickTarget = null

const getMaxPos = () => {
  const track = trackRef.value
  const viewport = viewportRef.value
  if (!track || !viewport) return 0
  return -(track.scrollWidth - viewport.offsetWidth)
}

const updateProgress = () => {
  const maxPos = getMaxPos()
  if (maxPos === 0) return
  const progress = Math.min(Math.max(position / maxPos, 0), 1)
  if (progressFillRef.value) gsap.set(progressFillRef.value, { scaleX: progress })
}

// ── Drag con física + rubber-band (todo transform, sin scrollLeft) ──
const startDrag = (e, clientX) => {
  if (inertiaFrame) cancelAnimationFrame(inertiaFrame)
  gsap.killTweensOf(trackRef.value)
  position = parseFloat(gsap.getProperty(trackRef.value, 'x')) || 0
  isDragging.value = true
  lastX = clientX
  lastTime = performance.now()
  velocity = 0
  dragDistance = 0
  clickTarget = e.target
  if (hintRef.value) gsap.to(hintRef.value, { opacity: 0, duration: 0.25 })
}

const moveDrag = (clientX) => {
  if (!isDragging.value) return
  const now = performance.now()
  const dt = now - lastTime
  if (dt > 0) velocity = ((lastX - clientX) / dt) * 16
  const delta = lastX - clientX
  dragDistance += Math.abs(delta)
  lastX = clientX
  lastTime = now

  position -= delta
  const maxPos = getMaxPos()

  let displayPos
  if (position > 0) {
    displayPos = position * 0.3 // borde derecho: estira a la derecha
  } else if (position < maxPos) {
    displayPos = maxPos + (position - maxPos) * 0.3 // borde izquierdo: estira a la izquierda
  } else {
    displayPos = position
  }

  gsap.set(trackRef.value, { x: displayPos })
  updateProgress()
}

const endDrag = () => {
  if (!isDragging.value) return
  isDragging.value = false

  if (dragDistance < 5) {
    const card = clickTarget?.closest('.testimonial-card')
    if (card) {
      const index = cardRefs.value.indexOf(card)
      if (index >= 0 && testimonials.value[index]) {
        selectedTestimonial.value = testimonials.value[index]
        modalOpen.value = true
      }
    }
    return
  }

  const maxPos = getMaxPos()

  // Fuera de rango → snap al borde
  if (position > 0 || position < maxPos) {
    const snapTo = position > 0 ? 0 : maxPos
    position = snapTo
    gsap.to(trackRef.value, { x: snapTo, duration: 0.5, ease: 'power3.out' })
    velocity = 0
    return
  }

  if (Math.abs(velocity) < 0.5) return

  const applyInertia = () => {
    velocity *= 0.95
    if (Math.abs(velocity) < 0.1) return
    position -= velocity
    const mp = getMaxPos()
    if (position > 0) {
      position = 0
      velocity = 0
    }
    if (position < mp) {
      position = mp
      velocity = 0
    }
    gsap.set(trackRef.value, { x: position })
    updateProgress()
    inertiaFrame = requestAnimationFrame(applyInertia)
  }
  inertiaFrame = requestAnimationFrame(applyInertia)
}

const onMouseDown = (e) => startDrag(e, e.clientX)
const onMouseMove = (e) => {
  if (isDragging.value) moveDrag(e.clientX)
}
const onMouseUp = () => endDrag()
const onTouchStart = (e) => startDrag(e, e.touches[0].clientX)
const onTouchMove = (e) => moveDrag(e.touches[0].clientX)

const openModal = (testimonial) => {
  selectedTestimonial.value = testimonial
  modalOpen.value = true
}

// ── Continuous scroll-based animation (same pattern as JourneyV2) ──
const animateElement = (element) => {
  if (!element) return

  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight

  const enterStart = windowHeight * 1.1
  const enterEnd = windowHeight * 0.85
  const exitStart = windowHeight * 0.15
  const exitEnd = -rect.height * 0.5

  let opacity = 1
  let blur = 0
  let y = 0

  if (rect.top > enterEnd) {
    const progress = Math.min(1, Math.max(0, (enterStart - rect.top) / (enterStart - enterEnd)))
    opacity = progress
    blur = (1 - progress) * 8
    y = (1 - progress) * 30
  } else if (rect.top < exitStart) {
    const progress = Math.min(1, Math.max(0, (exitStart - rect.top) / (exitStart - exitEnd)))
    opacity = Math.max(0.05, 1 - progress * 0.95)
    y = progress * -20
  }

  gsap.set(element, {
    opacity,
    filter: `blur(${blur}px)`,
    y,
    force3D: true,
  })
}

const handleScrollAnimation = () => {
  animateElement(headerRef.value)
  scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)
}

onMounted(() => {
  ctx = gsap.context(() => {
    // Sección: fade+scale suave al entrar
    gsap.fromTo(
      sectionRef.value,
      { opacity: 0.5, scale: 0.98 },
      {
        opacity: 1,
        scale: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top 90%',
          end: 'top 30%',
          scrub: true,
        },
      }
    )

    // Header
    gsap.from(headerRef.value, {
      y: 30,
      opacity: 0,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: { trigger: sectionRef.value, start: 'top 75%' },
    })

    // Hint
    gsap.fromTo(
      hintRef.value,
      { opacity: 0, x: -8 },
      {
        opacity: 1,
        x: 0,
        duration: 0.7,
        delay: 0.3,
        ease: 'power2.out',
        scrollTrigger: { trigger: sectionRef.value, start: 'top 75%' },
      }
    )
  }, sectionRef.value)

  // Cards: animación de entrada cuando la sección entra al viewport
  cardRefs.value.forEach((card, i) => {
    if (!card) return
    gsap.from(card, {
      opacity: 0,
      y: 20,
      duration: 0.5,
      delay: i * 0.06,
      ease: 'power2.out',
      scrollTrigger: { trigger: sectionRef.value, start: 'top 75%' },
    })
  })

  // Drag
  const vp = viewportRef.value
  vp.addEventListener('mousedown', onMouseDown)
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
  vp.addEventListener('touchstart', onTouchStart, { passive: true })
  vp.addEventListener('touchmove', onTouchMove, { passive: true })
  vp.addEventListener('touchend', endDrag)

  // Start scroll animation loop
  scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)
})

onUnmounted(() => {
  if (ctx) ctx.revert()
  if (inertiaFrame) cancelAnimationFrame(inertiaFrame)
  if (scrollAnimationFrame) cancelAnimationFrame(scrollAnimationFrame)
  if (observer) observer.disconnect()
  const vp = viewportRef.value
  if (vp) {
    vp.removeEventListener('mousedown', onMouseDown)
    vp.removeEventListener('touchstart', onTouchStart)
    vp.removeEventListener('touchmove', onTouchMove)
    vp.removeEventListener('touchend', endDrag)
  }
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
})
</script>

<style scoped>
/* ─── Section ──────────────────────────────────────────────── */
.testimonials-section {
  background: var(--color-background, var(--color-black));
  width: 100%;
}

.testimonials-inner {
  display: flex;
  flex-direction: column;
  padding: clamp(4rem, 8vh, 6rem) 0 clamp(2rem, 4vh, 3rem);
  gap: clamp(1.5rem, 3vh, 2rem);
}

/* ─── Header ───────────────────────────────────────────────── */
.testimonials-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 0 clamp(1.5rem, 5vw, 5rem);
  flex-shrink: 0;
  will-change: transform, opacity, filter;
}

.testimonials-header__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.testimonials-eyebrow {
  font-size: clamp(0.7rem, 1.2vw, 0.85rem);
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-electric-purple);
  max-width: 36ch;
  line-height: 1.5;
}

.testimonials-title {
  @apply font-madi text-4xl sm:text-5xl md:text-7xl font-bold lowercase;
  color: var(--color-electric-purple);
}

.testimonials-header__right {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.testimonials-progress {
  width: clamp(6rem, 12vw, 10rem);
  height: 2px;
  background: var(--color-border);
  border-radius: 999px;
  overflow: hidden;
}

.testimonials-progress__fill {
  height: 100%;
  width: 100%;
  background: var(--color-electric-purple);
  border-radius: 999px;
  transform-origin: left center;
  transform: scaleX(0);
}

/* ─── Viewport & Track ─────────────────────────────────────── */
.testimonials-viewport {
  overflow: hidden;
  display: flex;
  align-items: stretch;
  cursor: grab;
  user-select: none;
  mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 8%,
    black 92%,
    transparent 100%
  );
}
.testimonials-viewport.is-dragging {
  cursor: grabbing;
}
.testimonials-viewport.is-dragging * {
  pointer-events: none;
}

.testimonials-track {
  display: flex;
  align-items: stretch;
  gap: clamp(1rem, 1.5vw, 1.5rem);
  padding: 0.5rem clamp(1.5rem, 5vw, 5rem);
  will-change: transform;
}

/* ─── Cards ────────────────────────────────────────────────── */
.testimonial-card {
  flex: 0 0 clamp(480px, 68vw, 860px);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: clamp(1.5rem, 3vw, 2.5rem);
  border-radius: 1.5rem;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  position: relative;
  overflow: hidden;
  height: auto;
}

.testimonial-card__quote {
  flex: 1;
  font-size: clamp(0.9rem, 1.3vw, 1.05rem);
  line-height: 1.7;
  color: var(--color-text-primary);
  font-weight: 400;
  overflow: hidden;
  quotes: none;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
}

.testimonial-card__read-more {
  display: inline-flex;
  align-self: flex-start;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-electric-purple);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;
}

.testimonial-card__read-more:hover {
  color: var(--color-text-primary);
}

.testimonial-card__footer {
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border);
}

.testimonial-card__author {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.testimonial-card__sep {
  color: var(--color-text-muted);
  opacity: 0.4;
  flex-shrink: 0;
  font-size: 0.7rem;
}

.testimonial-card__name {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
}

.testimonial-card__name--link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  text-decoration: none;
  transition: color 0.2s ease;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
}

.testimonial-card__name--link:hover {
  color: #0077b5;
}

.testimonial-card__linkedin-icon {
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
}

.testimonial-card__role {
  font-size: 0.72rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.testimonial-card__company {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.testimonial-card__company-logo {
  height: 0.85rem;
  width: auto;
  object-fit: contain;
  opacity: 0.45;
  filter: grayscale(100%);
}

/* ─── Scroll hint ──────────────────────────────────────────── */
.testimonials-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.25rem clamp(1.5rem, 5vw, 5rem) 0;
  flex-shrink: 0;
}

.testimonials-hint__text {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.testimonials-hint__icon {
  width: 0.85rem;
  height: 0.85rem;
  color: var(--color-text-muted);
}

/* ─── Responsive ───────────────────────────────────────────── */
@media (max-width: 768px) {
  .testimonials-eyebrow {
    max-width: 24ch;
  }

  .testimonial-card {
    flex: 0 0 min(85vw, 340px);
  }

  .testimonial-card__quote {
    -webkit-line-clamp: 7;
  }
}

@media (max-width: 480px) {
  .testimonials-header {
    align-items: center;
    gap: 1rem;
  }

  .testimonials-header__right {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }

  .testimonials-progress {
    width: 8rem;
  }

  .testimonial-card {
    flex: 0 0 min(85vw, 340px);
  }

  .testimonial-card__quote {
    -webkit-line-clamp: 7;
  }
}
</style>
